import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TOKEN_KEY = "skope_admin_token";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot | sent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      navigate("/admin", { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setMode("sent");
    } catch {
      setMode("sent"); // never reveal whether the email is authorised
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white bg-blueprint-fine flex items-center justify-center px-6">
      <div className="w-full max-w-md border border-ink/10 bg-[#f6f6f6] p-10" data-testid="admin-login-form">
        <img src="/assets/skope-logo-full.png" alt="Skope Kitchens" className="h-7 w-auto" />
        <h1 className="mt-8 font-display font-semibold text-2xl text-ink">Blog Admin</h1>
        <p className="mt-2 text-sm text-ink/45">Private access for the authorised Skope Kitchens administrator.</p>

        {mode === "login" && (
          <form onSubmit={submitLogin}>
            <label className="block mt-8">
              <span className="font-mono text-[10px] tracking-[0.25em] text-ink/45 uppercase">Email Address</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid="admin-login-email"
                className="mt-2 w-full bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors" />
            </label>
            <label className="block mt-6">
              <span className="font-mono text-[10px] tracking-[0.25em] text-ink/45 uppercase">Password</span>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                data-testid="admin-login-password"
                className="mt-2 w-full bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors" />
            </label>
            {error && <p className="mt-4 text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
            <button type="submit" disabled={loading}
              data-testid="admin-login-submit"
              className="mt-8 w-full bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase py-4 hover:bg-ink hover:text-white transition-colors disabled:opacity-50">
              {loading ? "Signing in…" : "Login"}
            </button>
            <button type="button" onClick={() => { setMode("forgot"); setError(""); }}
              className="mt-4 w-full text-center font-mono text-[10px] tracking-[0.2em] uppercase text-ink/40 hover:text-skope transition-colors"
              data-testid="forgot-password-link">
              Forgot password?
            </button>
          </form>
        )}

        {mode === "forgot" && (
          <form onSubmit={submitForgot}>
            <p className="mt-6 text-sm text-ink/50 leading-relaxed">
              Enter the administrator email address. If it is authorised, a secure one-time reset link will be emailed to it.
            </p>
            <label className="block mt-6">
              <span className="font-mono text-[10px] tracking-[0.25em] text-ink/45 uppercase">Email Address</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid="forgot-email"
                className="mt-2 w-full bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors" />
            </label>
            <button type="submit" disabled={loading}
              data-testid="forgot-submit"
              className="mt-8 w-full bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase py-4 hover:bg-ink hover:text-white transition-colors disabled:opacity-50">
              {loading ? "Sending…" : "Send Reset Link"}
            </button>
            <button type="button" onClick={() => setMode("login")}
              className="mt-4 w-full flex items-center justify-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-ink/40 hover:text-skope transition-colors"
              data-testid="forgot-back">
              <ArrowLeft size={12} /> Back to login
            </button>
          </form>
        )}

        {mode === "sent" && (
          <div data-testid="forgot-sent">
            <p className="mt-6 text-sm text-ink/55 leading-relaxed">
              If this email is an authorised administrator, a reset link has been sent. The link works once and expires in 30 minutes.
            </p>
            <button onClick={() => { setMode("login"); setEmail(""); }}
              className="mt-8 w-full border border-ink/20 font-mono text-xs tracking-[0.2em] uppercase py-4 text-ink hover:border-skope hover:text-skope transition-colors"
              data-testid="forgot-done">
              Back to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;
